import React from 'react';
import styled from 'styled-components';
import BaseCard, { BaseCardProps } from '../card/base-card';
import { Div } from '../box';
import colors from '../../style/colors';
import { variants } from './style';

export interface FlipCardProps extends BaseCardProps {
  variant?: keyof typeof variants;
  selected?: boolean;
  onClick?: () => void;
  backComponent: React.ReactNode;
  frontComponent: React.ReactNode;
  width?: string | number;
  minWidth?: string | number;
  height?: string | number;
  minHeight?: string | number;
  borderColor?: string;
  borderWidth?: string | number;
  borderStyle?: string;
  borderRadius?: 'sm' | 'md' | 'lg' | 'round' | 'pill' | 'none';
}

// generated by claude
const FlipCardContainer = styled(BaseCard)<{
  isFlipped: boolean;
  width?: string | number;
  minWidth?: string | number;
  height?: string | number;
  minHeight?: string | number;
}>`
  width: ${props => props.width || '300px'};
  min-width: ${props => props.minWidth || 'auto'};
  height: ${props => props.height || 'auto'};
  min-height: ${props => props.minHeight || '140px'};
  perspective: 1000px;
  cursor: pointer;
  background: transparent;
  border: none;
  box-shadow: none;

  &:hover {
    box-shadow: none;
  }
`;

const CardInner = styled(Div)<{ isFlipped: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  text-align: center;
  transform-style: preserve-3d;
  transition: transform 0.6s ease;
  transform: ${props => (props.isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)')};
`;

const CardFace = styled(Div)<{
  variant?: keyof typeof variants;
  selected?: boolean;
  minHeight?: string | number;
  borderColor?: string;
  borderWidth?: string | number;
  borderStyle?: string;
  borderRadius?: string | number;
}>`
  position: absolute;
  width: 100%;
  min-height: ${props => props.minHeight || '100px'};
  backface-visibility: hidden;
  ${props => (props.variant ? variants[props.variant] : variants.white)};
  opacity: ${props => (props.selected ? 1.0 : 0.5)};
  border: ${props => {
    const width = props.borderWidth || '1px';
    const style = props.borderStyle || 'solid';
    const color = props.borderColor
      ? colors[props.borderColor as keyof typeof colors] || props.borderColor
      : colors.gray300;
    return `${width} ${style} ${color}`;
  }};
  border-radius: ${props => props.borderRadius || '8px'};
  box-shadow: 0px 0px 10px 0px #0000000d;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0;
  transition:
    box-shadow 0.3s ease,
    opacity 0.3s ease;
  color: ${colors.white};

  ${FlipCardContainer}:hover & {
    box-shadow: 0px 0px 30px 0px #0000000d;
  }
`;

const CardFront = styled(CardFace)`
  z-index: 2;
`;

const CardBack = styled(CardFace)`
  transform: rotateY(180deg);
  z-index: 1;
  opacity: 1 !important;
`;

const FlipCard: React.FC<FlipCardProps> = ({
  variant,
  selected = false,
  onClick,
  backComponent,
  frontComponent,
  width,
  minWidth,
  height,
  minHeight,
  borderColor,
  borderWidth,
  borderStyle,
  borderRadius,
  ...rest
}) => {
  const [isFlipped, setIsFlipped] = React.useState(false);

  const handleMouseEnter = () => {
    setIsFlipped(true);
  };

  const handleMouseLeave = () => {
    setIsFlipped(false);
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <FlipCardContainer
      isFlipped={isFlipped}
      width={width}
      minWidth={minWidth}
      height={height}
      minHeight={minHeight}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label="Flip card"
      {...rest}
    >
      <CardInner isFlipped={isFlipped}>
        <CardFront
          variant={variant}
          selected={selected}
          minHeight={minHeight}
          borderColor={borderColor}
          borderWidth={borderWidth}
          borderStyle={borderStyle}
          borderRadius={borderRadius}
        >
          {frontComponent}
        </CardFront>
        <CardBack
          variant={variant}
          selected={selected}
          minHeight={minHeight}
          borderColor={borderColor}
          borderWidth={borderWidth}
          borderStyle={borderStyle}
          borderRadius={borderRadius}
        >
          {backComponent}
        </CardBack>
      </CardInner>
    </FlipCardContainer>
  );
};

export default FlipCard;
